import React, { useEffect, useState } from 'react';
import AOCSignModal from './AOCSignModal';
import SanctionForTestModal from './SanctionForTestModal';
import {
  markAOCSigned,
  markRPSigned,
  markSFTRevoked,
  markWorkComplete,
  notifySFTRevoke,
  rejectTestSanction,
} from '../../../services/lvWorkPermitFlowService';
import { getAppointedPersonsLV } from '../../../services/plantSafetyWorkPermitService';
import ConfirmationModal from '../../../components/RejectButton/ConfirmationModal';

interface Status {
  id: number;
  code: string;
  title: string;
  color: string;
  created_at: string;
  updated_at: string;
}

interface SanctionDetails {
  id: number;
  lv_work_permit_id: number;
  plant_code_id: number;
  job_number: string;
  work: string;
  allowed_work: string;
  isolation: string;
  init_signal: string;
  halt_signal: string;
  isolation_signal: string;
  status_id: number;
  created_at: string;
  updated_at: string;
  status: Status;
}

interface Sanction {
  details: SanctionDetails;
  status: Status;
}
interface Props {
  sanction: Sanction;
  plantName?: string;
  permitNumber?: string;
  jobNumber?: string;
  permitId: number;
  fetchDetail: any;
  createdBy: any;
  currentUserID: any;
  canAOCSign: any;
  canSFTRevoke: any;
  onDownload?: () => void;
  downloadLoading?: boolean;
}

/**
 * Lightweight sanitizer for Quill-generated HTML (no external dependency).
 * Strips <script>/<style> tags, inline event handlers (onclick, onerror, etc.),
 * and javascript:/data: URLs. Also forces safe target/rel on links.
 */
const sanitizeHtml = (html?: string): string => {
  if (!html) return '';

  let clean = html;

  clean = clean.replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '');
  clean = clean.replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  clean = clean.replace(/(href|src)\s*=\s*(["'])\s*(javascript:|data:)[^"']*\2/gi, '$1="#"');
  clean = clean.replace(/<a\s+((?:(?!target=)[^>])*)>/gi, '<a target="_blank" rel="noopener noreferrer" $1>');

  return clean;
};

// Must stay identical to the classes in SanctionForTestModal.tsx so rich text
// (bullets, numbered lists, links, headings) renders the same everywhere it's shown.
const richTextDisplayClasses =
  'text-sm [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 ' +
  '[&_li]:mb-1 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_a]:text-blue-600 [&_a]:underline ' +
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_strong]:font-semibold [&_em]:italic [&_u]:underline';

const RichTextDisplay = ({ html }: { html?: string }) => (
  <div className={richTextDisplayClasses} dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
);

const getSanctionReferenceNumber = (permitNumber?: string) => permitNumber?.replace(/^#?WPS/i, 'SFT');

const SanctionContent: React.FC<Props> = ({
  sanction,
  plantName = '-',
  permitNumber,
  jobNumber,
  permitId,
  fetchDetail,
  createdBy,
  currentUserID,
  canAOCSign,
  canSFTRevoke,
  onDownload,
  downloadLoading = false,
}) => {
  const [showAOCSignModal, setShowAOCSignModal] = useState(false);
  const [showEditSanctionModal, setShowEditSanctionModal] = useState(false);

  const [showAOCSignConfirmModal, setShowAOCSignConfirmModal] = useState(false);
  const [signLoading, setSignLoading] = useState(false);

  const [showRPSignModal, setShowRPSignModal] = useState(false);
  const [rpSignLoading, setRPSignLoading] = useState(false);

  const [showWorkCompleteModal, setShowWorkCompleteModal] = useState(false);
  const [workCompleteLoading, setWorkCompleteLoading] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const [showSFTRevokeModal, setShowSFTRevokeModal] = useState(false);
  const [sftRevokeLoading, setSFTRevokeLoading] = useState(false);

  const [showNotifyRevokeModal, setShowNotifyRevokeModal] = useState(false);
  const [appointedPersons, setAppointedPersons] = useState<any[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (showNotifyRevokeModal) {
      loadAppointedPersons();
    }
  }, [showNotifyRevokeModal]);

  const loadAppointedPersons = async () => {
    try {
      const response = await getAppointedPersonsLV();
      setAppointedPersons(response?.data || response || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleNotifyRevoke = async () => {
    if (!selectedPerson) {
      alert('Please select an appointed person');
      return;
    }

    try {
      setLoading(true);

      await notifySFTRevoke(permitId, {
        user_ids: [selectedPerson.id],
      });

      setShowNotifyRevokeModal(false);
      setSelectedPerson(null);

      fetchDetail?.();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAOCSign = async () => {
    try {
      setSignLoading(true);

      await markAOCSigned(permitId);

      setShowAOCSignConfirmModal(false);

      // Refresh details
      fetchDetail?.();
    } catch (error) {
      console.error('Error signing permit:', error);
    } finally {
      setSignLoading(false);
    }
  };

  const handleRPSign = async () => {
    try {
      setRPSignLoading(true);

      await markRPSigned(permitId);

      setShowRPSignModal(false);

      // Refresh details
      fetchDetail?.();
    } catch (error) {
      console.error('Error signing off permit:', error);
    } finally {
      setRPSignLoading(false);
    }
  };

  const handleWorkComplete = async () => {
    try {
      setWorkCompleteLoading(true);

      await markWorkComplete(permitId);

      setShowWorkCompleteModal(false);

      // Refresh details
      fetchDetail?.();
    } catch (error) {
      console.error('Error marking work complete:', error);
    } finally {
      setWorkCompleteLoading(false);
    }
  };

  const handleRejectTestSanction = async (remarks: string) => {
    try {
      setRejectLoading(true);
      await rejectTestSanction(permitId, { remarks });
      setShowRejectModal(false);
      fetchDetail?.();
    } catch (error) {
      console.error('Error rejecting test sanction:', error);
    } finally {
      setRejectLoading(false);
    }
  };

  const handleSFTRevoke = async () => {
    try {
      setSFTRevokeLoading(true);

      await markSFTRevoked(permitId);

      setShowSFTRevokeModal(false);

      fetchDetail?.();
    } catch (error) {
      console.error('Error revoking sanction:', error);
    } finally {
      setSFTRevokeLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-[#1A2B6D]">#{getSanctionReferenceNumber(permitNumber)}</h2>
          <span className="text-purple-600 text-sm font-medium">● {sanction.status.title}</span>
        </div>

        {sanction.status.code === 'NEW' && createdBy === currentUserID && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowEditSanctionModal(true)}
              className="px-4 py-2 border border-blue-500 text-blue-600 hover:bg-blue-50 text-sm font-medium rounded-md transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => setShowAOCSignModal(true)}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
            >
              Notify to Sign
            </button>
          </div>
        )}
        {canAOCSign && sanction.status.title === 'Sign Pending' && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowRejectModal(true)}
              className="px-4 py-2 border border-red-600 text-red-600 hover:bg-red-50 text-sm font-medium rounded-md transition-colors"
            >
              Reject
            </button>
            <button
              onClick={() => setShowAOCSignConfirmModal(true)}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
            >
              Sign
            </button>
          </div>
        )}
        {sanction.status.title === 'Signed Off (Appointed Person)' && createdBy === currentUserID && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowRejectModal(true)}
              className="px-4 py-2 border border-red-600 text-red-600 hover:bg-red-50 text-sm font-medium rounded-md transition-colors"
            >
              Reject
            </button>
            <button
              onClick={() => setShowRPSignModal(true)}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
            >
              Sign Off
            </button>
          </div>
        )}
        {sanction.status.title === 'Signed Off (Responsible Person)' && createdBy === currentUserID && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowRejectModal(true)}
              className="px-4 py-2 border border-red-600 text-red-600 hover:bg-red-50 text-sm font-medium rounded-md transition-colors"
            >
              Reject
            </button>
            <button
              onClick={() => setShowWorkCompleteModal(true)}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
            >
              Sign Off Work Completion
            </button>
          </div>
        )}
        {sanction.status.title === 'Work Completed' && createdBy === currentUserID && (
          <button
            onClick={() => setShowNotifyRevokeModal(true)}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
          >
            Notify to Revoke
          </button>
        )}
        {/* A pending SFT_REVOKE assignment is the authoritative revoke-stage
            permission. Do not additionally gate this by a display title,
            which can differ between environments or translated seed data. */}
        {canSFTRevoke && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowRejectModal(true)}
              className="px-4 py-2 border border-red-600 text-red-600 hover:bg-red-50 text-sm font-medium rounded-md transition-colors"
            >
              Reject SFT
            </button>
            <button
              onClick={() => setShowSFTRevokeModal(true)}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
            >
              Revoke
            </button>
          </div>
        )}
        {sanction.status.title === 'Revoked' && onDownload && (
          <button
            type="button"
            onClick={onDownload}
            disabled={downloadLoading}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            {downloadLoading ? 'Downloading...' : 'Download'}
          </button>
        )}
      </div>

      <h3 className="text-blue-600 font-semibold mb-5">Sanction Details</h3>

      <div className="grid grid-cols-2 gap-8 mb-6">
        <div>
          <p className="text-gray-500 text-sm mb-1">Plant</p>
          <p className="font-medium">{plantName}</p>
        </div>

        <div>
          <p className="text-gray-500 text-sm mb-1">Job Number</p>
          <p>{sanction.details.job_number}</p>
        </div>
      </div>

      <div className="mb-6">
        <p className="text-gray-500 mb-2">Work to be Carried Out</p>
        <RichTextDisplay html={sanction.details.work} />
      </div>

      <div className="mb-6">
        <p className="text-gray-500 mb-2">Work permissible under this sanction for test</p>
        <RichTextDisplay html={sanction.details.allowed_work} />
      </div>

      <div className="mb-6">
        <p className="text-gray-500 mb-2">Isolation</p>
        <RichTextDisplay html={sanction.details.isolation} />
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div>
          <p className="text-gray-500 text-sm mb-1">Initial Signal</p>
          <RichTextDisplay html={sanction.details.init_signal} />
        </div>

        <div>
          <p className="text-gray-500 text-sm mb-1">Halt Signal</p>
          <RichTextDisplay html={sanction.details.halt_signal} />
        </div>

        <div>
          <p className="text-gray-500 text-sm mb-1">Isolation Signal</p>
          <RichTextDisplay html={sanction.details.isolation_signal} />
        </div>
      </div>

      {showAOCSignConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6">
            <h2 className="text-lg font-semibold mb-3">Confirm Sign</h2>

            <p className="text-gray-600 mb-6">Are you sure you want to sign this sanction?</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAOCSignConfirmModal(false)}
                disabled={signLoading}
                className="px-5 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleAOCSign}
                disabled={signLoading}
                className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
              >
                {signLoading ? 'Signing...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <ConfirmationModal
          title="Reject Test Sanction"
          message="Are you sure you want to reject this test sanction?"
          onConfirm={handleRejectTestSanction}
          onCancel={() => setShowRejectModal(false)}
          loading={rejectLoading}
          confirmText="Reject"
          loadingText="Rejecting..."
        />
      )}

      {showRPSignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6">
            <h2 className="text-lg font-semibold mb-3">Confirm Sign Off</h2>

            <p className="text-gray-600 mb-6">Are you sure you want to sign off this sanction?</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRPSignModal(false)}
                disabled={rpSignLoading}
                className="px-5 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleRPSign}
                disabled={rpSignLoading}
                className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
              >
                {rpSignLoading ? 'Signing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showWorkCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6">
            <h2 className="text-lg font-semibold mb-3">Confirm Work Completion</h2>

            <p className="text-gray-600 mb-6">Are you sure you want to sign off work completion?</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowWorkCompleteModal(false)}
                disabled={workCompleteLoading}
                className="px-5 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleWorkComplete}
                disabled={workCompleteLoading}
                className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
              >
                {workCompleteLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotifyRevokeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[480px] rounded-xl shadow-xl p-6 relative">
            <button onClick={() => setShowNotifyRevokeModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
              X
            </button>

            <h2 className="text-lg font-semibold mb-5">Notify to Revoke</h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Appointed Person</label>

              <select
                value={selectedPerson?.id || ''}
                onChange={(e) => {
                  const person = appointedPersons.find((p) => p.id === Number(e.target.value));
                  setSelectedPerson(person);
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select Appointed Person</option>

                {appointedPersons.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNotifyRevokeModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleNotifyRevoke}
                disabled={!selectedPerson || loading}
                className={`px-6 py-2 rounded-md text-white ${
                  selectedPerson && !loading ? 'bg-red-500 hover:bg-red-600' : 'bg-red-300 cursor-not-allowed'
                }`}
              >
                {loading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSFTRevokeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6">
            <h2 className="text-lg font-semibold mb-3">Confirm Revoke</h2>

            <p className="text-gray-600 mb-6">Are you sure you want to revoke this sanction?</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSFTRevokeModal(false)}
                disabled={sftRevokeLoading}
                className="px-5 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleSFTRevoke}
                disabled={sftRevokeLoading}
                className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
              >
                {sftRevokeLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AOCSignModal
        isOpen={showAOCSignModal}
        onClose={() => setShowAOCSignModal(false)}
        permitId={permitId}
        onSuccess={() => {
          setShowAOCSignModal(false);
          fetchDetail();
        }}
      />

      <SanctionForTestModal
        open={showEditSanctionModal}
        onClose={() => setShowEditSanctionModal(false)}
        permitData={{ id: permitId, job_number: jobNumber }}
        initialData={sanction.details}
        onSubmit={() => {
          setShowEditSanctionModal(false);
          fetchDetail?.();
        }}
      />
    </div>
  );
};

export default SanctionContent;
